import { useState } from 'react';
import { useActivity } from '@/api/activity';
import { fill } from '@/i18n/t';
import { Button } from '@/shell/ui/button';
import { StatusBadge, type StatusTone } from '@/shell/ui/status-badge';
import {
  type Activity,
  brauchtBetreuer,
  ergebnisSatz,
  laufSatz,
  problemSatz,
  sortiereProbleme,
} from '@/vorgang/abbildungen';
import { VORGANG_TEXTE } from '@/vorgang/texte';

const T = VORGANG_TEXTE.streifen;

type Eintrag = { key: string; tone: StatusTone; badge: string; text: string };

/**
 * Einträge in fester Reihenfolge: was gerade läuft, dann Probleme (das Wichtigste zuerst), dann die letzten Ergebnisse.
 * Pausen gehen von selbst weiter („Pausiert“), fehlende Einrichtung braucht den Betreuer.
 */
export function eintraegeAus(activity: Activity, now: Date = new Date()): Eintrag[] {
  const laufend = activity.running.map(
    (run, index): Eintrag => ({
      key: `lauf-${run.kind}-${run.refId ?? index}`,
      tone: 'notice',
      badge: T.laeuft,
      text: laufSatz(run),
    }),
  );
  const probleme = sortiereProbleme(activity.problems).map(
    (problem): Eintrag => ({
      key: `problem-${problem.code}`,
      tone: brauchtBetreuer(problem) ? 'serious' : 'notice',
      badge: brauchtBetreuer(problem)
        ? T.braucht
        : problem.code.endsWith('_paused')
          ? T.pausiert
          : T.hinweis,
      text: problemSatz(problem, now),
    }),
  );
  const fertig = activity.recent.map(
    (recent, index): Eintrag => ({
      key: `fertig-${recent.kind}-${recent.refId ?? index}-${recent.finishedAt}`,
      tone: recent.outcome === 'done' ? 'good' : 'notice',
      badge: recent.outcome === 'done' ? T.fertig : T.hinweis,
      text: ergebnisSatz(recent, now),
    }),
  );
  return [...laufend, ...probleme, ...fertig];
}

function Zeile({ eintrag }: { eintrag: Eintrag }) {
  return (
    <>
      <StatusBadge tone={eintrag.tone} label={eintrag.badge} className="shrink-0" />
      <span className="min-w-0 flex-1 text-body text-ink">{eintrag.text}</span>
    </>
  );
}

/**
 * Streifen „Was passiert gerade“ auf jeder Seite (Handoff Schritt 2): eine Zeile mit dem Wichtigsten, auf Wunsch alle
 * laufenden Vorgänge, Hinweise und Ergebnisse der letzten 24 Stunden. Fehler, die bisher still blieben (nicht
 * eingerichtet, pausiert, gestört), stehen hier im Klartext. Ohne Einträge oder bei einer Störung beim Laden: nichts.
 */
export function ActivityStrip() {
  const query = useActivity();
  const [offen, setOffen] = useState(false);
  if (query.data === undefined) {
    return null;
  }
  const eintraege = eintraegeAus(query.data);
  const [erster, ...rest] = eintraege;
  if (erster === undefined) {
    return null;
  }
  return (
    <section aria-label={T.region} className="border-b border-line bg-surface-sunken px-4 py-2">
      <div className="mx-auto grid max-w-[1200px] gap-2 lg:px-6">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <Zeile eintrag={erster} />
          {rest.length === 0 ? null : (
            <Button
              variant="quiet"
              aria-expanded={offen}
              aria-controls="aktivitaet-mehr"
              onClick={() => setOffen((wert) => !wert)}
            >
              {offen ? T.weniger : fill(T.mehr, { anzahl: rest.length })}
            </Button>
          )}
        </div>
        {offen && rest.length > 0 ? (
          <ul id="aktivitaet-mehr" className="grid gap-2 pb-1">
            {rest.map((eintrag) => (
              <li key={eintrag.key} className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <Zeile eintrag={eintrag} />
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </section>
  );
}
